/**
 * Fiche de révision d'un pack (migration 0723, RPC `get_pack_revision_sheet`).
 *
 * La fiche n'est pas un contenu rédigé : c'est une COMPILATION des SKILLCARDS du pack
 * (règle, tableau, exemple, piège), dans l'ordre du pack, avec l'état de maîtrise de l'élève.
 *
 * ⚠️ Le paywall est SERVEUR : la RPC n'envoie le contenu que des cartes auxquelles l'élève a
 * accès. Une carte verrouillée arrive SANS ses clés de contenu — on ne masque rien ici, il n'y a
 * rien à masquer. Ne jamais déduire l'accès d'un affichage conditionnel côté client.
 *
 * L'état de maîtrise réutilise le vocabulaire déjà en place (`SKILLCARD_MASTERED_THRESHOLD`,
 * convention `usage_count > 0` = déjà travaillée) : un seul seuil dans le projet.
 *
 * Fonctions PURES, testées : `masteryStateOf`, `revisionSheetStats`.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { parseCardContent, type CardSection } from '@jeraisonne/db/cards';
import { schoolMonthRank, sortPacks } from '@jeraisonne/db/curriculum';
import { SKILLCARD_MASTERED_THRESHOLD } from './student-skillcards';

const db = supabase as unknown as SupabaseClient;

/**
 * Les TROIS états, et pas deux : sur un pack réellement travaillé, la majorité des cartes n'a
 * aucune donnée de progression (cf. claude/plan-fiche-de-revision.md §4). Confondre « jamais
 * travaillée » et « fragile » afficherait des cartes faussement en alerte.
 */
export type MasteryState = 'untouched' | 'fragile' | 'mastered';

export const MASTERY_LABELS: Record<MasteryState, string> = {
  untouched: 'Pas encore travaillée',
  fragile: 'À revoir',
  mastered: 'Acquise',
};

export interface RevisionSheetCard {
  id: string;
  short_code: string;
  title: string;
  domain_label: string | null;
  levels: string[];
  position: number;
  mastery: number;
  usage_count: number;
  locked: boolean;
  owned: boolean;
  assigned: boolean;
  /** Absents quand `locked` : le serveur ne les envoie pas. */
  when_to_use: string | null;
  rule: string | null;
  pitfalls: string | null;
  sections: CardSection[];
}

export interface RevisionSheet {
  pack: {
    id: string;
    slug: string | null;
    title: string;
    hook: string | null;
    level: string | null;
    subject: string | null;
  };
  student: { id: string; display_name: string | null; deck_expires_at: string | null };
  access_reason: string;
  card_count: number;
  unlocked_count: number;
  generated_at: string;
  cards: RevisionSheetCard[];
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

export function normalizeRevisionSheetCard(raw: Record<string, unknown>): RevisionSheetCard {
  return {
    id: String(raw.id),
    short_code: String(raw.short_code ?? ''),
    title: String(raw.title ?? 'SKILLCARD'),
    domain_label: str(raw.domain_label),
    levels: Array.isArray(raw.levels) ? (raw.levels as string[]) : [],
    position: num(raw.position),
    mastery: num(raw.mastery),
    usage_count: num(raw.usage_count),
    locked: raw.locked === true,
    owned: raw.owned === true,
    assigned: raw.assigned === true,
    when_to_use: str(raw.when_to_use),
    rule: str(raw.rule),
    pitfalls: str(raw.pitfalls),
    // Lecture tolérante : un `structured_content` invalide ⇒ aucune section, jamais d'écran cassé.
    sections: raw.structured_content ? parseCardContent(raw.structured_content).sections : [],
  };
}

export function normalizeRevisionSheet(raw: Record<string, unknown>): RevisionSheet {
  const pack = (raw.pack ?? {}) as Record<string, unknown>;
  const student = (raw.student ?? {}) as Record<string, unknown>;
  const cards = (Array.isArray(raw.cards) ? raw.cards : []) as Record<string, unknown>[];
  return {
    pack: {
      id: String(pack.id ?? ''),
      slug: str(pack.slug),
      title: String(pack.title ?? 'Pack'),
      hook: str(pack.hook),
      level: str(pack.level),
      subject: str(pack.subject),
    },
    student: {
      id: String(student.id ?? ''),
      display_name: str(student.display_name),
      deck_expires_at: str(student.deck_expires_at),
    },
    access_reason: String(raw.access_reason ?? 'no_access'),
    card_count: num(raw.card_count, cards.length),
    unlocked_count: num(raw.unlocked_count),
    generated_at: str(raw.generated_at) ?? new Date().toISOString(),
    cards: cards.map(normalizeRevisionSheetCard),
  };
}

/** Jamais travaillée → `untouched`, quelle que soit la maîtrise stockée (0 par défaut). */
export function masteryStateOf(
  card: Pick<RevisionSheetCard, 'mastery' | 'usage_count'>,
): MasteryState {
  if (card.usage_count <= 0) return 'untouched';
  return card.mastery >= SKILLCARD_MASTERED_THRESHOLD ? 'mastered' : 'fragile';
}

export interface RevisionSheetStats {
  total: number;
  unlocked: number;
  locked: number;
  mastered: number;
  fragile: number;
  untouched: number;
}

export function revisionSheetStats(cards: readonly RevisionSheetCard[]): RevisionSheetStats {
  const open = cards.filter((c) => !c.locked);
  const by = (s: MasteryState) => open.filter((c) => masteryStateOf(c) === s).length;
  return {
    total: cards.length,
    unlocked: open.length,
    locked: cards.length - open.length,
    mastered: by('mastered'),
    fragile: by('fragile'),
    untouched: by('untouched'),
  };
}

/** Les cartes à revoir d'abord : c'est ce que l'élève vient chercher avant un contrôle. */
export function fragileFirst(cards: readonly RevisionSheetCard[]): RevisionSheetCard[] {
  const rank: Record<MasteryState, number> = { fragile: 0, untouched: 1, mastered: 2 };
  return [...cards].sort(
    (a, b) =>
      rank[masteryStateOf(a)] - rank[masteryStateOf(b)] ||
      a.mastery - b.mastery ||
      a.position - b.position,
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export const packRevisionSheetKey = (packId: string, studentId: string | null) =>
  ['pack-revision-sheet', packId, studentId ?? 'me'] as const;

/** `studentId` non nul : lecture par un tuteur lié ou un admin (même censure que pour l'élève). */
export function usePackRevisionSheet(packId: string | null | undefined, studentId?: string | null) {
  return useQuery({
    queryKey: packRevisionSheetKey(packId ?? '', studentId ?? null),
    enabled: !!packId,
    staleTime: 30_000,
    queryFn: async (): Promise<RevisionSheet | null> => {
      const { data, error } = await db.rpc('get_pack_revision_sheet', {
        p_pack_id: packId,
        ...(studentId ? { p_student_id: studentId } : {}),
      });
      if (error) throw new Error(error.message);
      if (!data) return null;
      return normalizeRevisionSheet(data as Record<string, unknown>);
    },
  });
}

// ── Packs dont on peut proposer la fiche (RPC 0744) ──────────────────────────

export interface RevisionSheetPack {
  id: string;
  title: string;
  subject: string;
  start_month: number;
  end_month: number | null;
  position: number;
  card_count: number;
  mastered_count: number;
}

export function normalizeRevisionSheetPack(raw: Record<string, unknown>): RevisionSheetPack {
  return {
    id: String(raw.id),
    title: String(raw.title ?? 'Pack'),
    subject: String(raw.subject ?? ''),
    start_month: num(raw.start_month, 9),
    end_month: typeof raw.end_month === 'number' ? raw.end_month : null,
    position: num(raw.position),
    card_count: num(raw.card_count),
    mastered_count: num(raw.mastered_count),
  };
}

export const revisionSheetPacksKey = (studentId: string | null) =>
  ['revision-sheet-packs', studentId ?? 'me'] as const;

/**
 * Les packs du niveau de l'élève. `studentId` non nul : lecture par son prof lié,
 * son parent lié ou un admin (même garde que la fiche elle-même).
 */
export function useRevisionSheetPacks(studentId?: string | null, enabled = true) {
  return useQuery({
    queryKey: revisionSheetPacksKey(studentId ?? null),
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<RevisionSheetPack[]> => {
      const { data, error } = await db.rpc('get_revision_sheet_packs', {
        ...(studentId ? { p_student_id: studentId } : {}),
      });
      if (error) throw new Error(error.message);
      return ((data ?? []) as Record<string, unknown>[]).map(normalizeRevisionSheetPack);
    },
  });
}

/**
 * Ce qu'il y a à réviser : les packs déjà abordés (jusqu'au pack en cours inclus),
 * groupés par matière, le plus récent d'abord. Un pack à venir n'a rien à réviser —
 * et un niveau de Terminale compte plus de 50 packs, qu'on ne déverse pas à l'écran.
 */
export function coveredPacksBySubject(
  packs: readonly RevisionSheetPack[],
  today = new Date(),
): { subject: string; packs: RevisionSheetPack[] }[] {
  // Pas `currentPackIndex` : il retombe sur le PREMIER pack quand aucun n'a
  // commencé (utile pour « Mon année », faux ici — il n'y a alors rien à réviser).
  const now = schoolMonthRank(today.getMonth() + 1);
  const started = (p: RevisionSheetPack) => schoolMonthRank(p.start_month) <= now;
  const subjects = [...new Set(packs.map((p) => p.subject))];
  return subjects
    .map((subject) => ({
      subject,
      packs: sortPacks(packs.filter((p) => p.subject === subject && started(p))).reverse(),
    }))
    .filter((g) => g.packs.length > 0);
}
